import React, { useState, useEffect } from 'react';
import { CheckCircleOutlined, CloseOutlined, FileTextOutlined } from '@ant-design/icons';

/**
 * Lessons Learned banner component that matches TaskAssignmentNotification styling
 * Displays when crisis status changes to "Resolved"
 */
const LessonsLearnedBanner = ({ 
  incidentId, 
  onComplete, 
  onSkip, 
  topOffset = 0,
  bannerId, 
  registerBanner, 
  unregisterBanner, 
  calculatePosition 
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [dynamicPosition, setDynamicPosition] = useState(topOffset);

  // Register banner and set dynamic position
  useEffect(() => {
    if (bannerId && registerBanner && unregisterBanner && calculatePosition) {
      // Register this banner as visible
      registerBanner(bannerId);
      
      // Calculate and set dynamic position
      const position = calculatePosition(bannerId);
      setDynamicPosition(position);
      
      return () => {
        // Unregister banner when component unmounts
        unregisterBanner(bannerId);
      };
    }
  }, [bannerId, registerBanner, unregisterBanner, calculatePosition]);

  // Update position when other banners change
  useEffect(() => {
    if (isVisible && bannerId && calculatePosition) {
      const position = calculatePosition(bannerId);
      setDynamicPosition(position);
      console.log(`🎯 LessonsLearnedBanner: Position updated to ${position}px`);
    }
  }, [isVisible, bannerId, calculatePosition]);

  const handleComplete = () => {
    setIsVisible(false);
    if (bannerId && unregisterBanner) {
      unregisterBanner(bannerId);
    }
    setTimeout(() => {
      onComplete();
    }, 300); // Allow fade-out animation to complete
  };

  const handleSkip = () => {
    setIsVisible(false);
    if (bannerId && unregisterBanner) {
      unregisterBanner(bannerId);
    }
    setTimeout(() => {
      onSkip();
    }, 300); // Allow fade-out animation to complete
  };

  if (!isVisible) {
    return null;
  }

  return (
    <>
      <style>
        {`
          @keyframes slide-down-lessons {
            from {
              transform: translateY(-100%);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
          
          @keyframes slide-up-lessons {
            from {
              transform: translateY(0);
              opacity: 1;
            }
            to {
              transform: translateY(-100%);
              opacity: 0;
            }
          }
          
          .lessons-banner-enter {
            animation: slide-down-lessons 0.4s ease-out;
          }
          
          .lessons-banner-exit {
            animation: slide-up-lessons 0.3s ease-in;
          }
        `}
      </style>
      
      <div 
        className="lessons-banner-enter"
        style={{
          position: 'fixed',
          top: `${dynamicPosition}px`,
          left: 0,
          right: 0,
          zIndex: 9998,
          backgroundColor: '#52c41a',
          color: 'white',
          padding: '12px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: 'none',
          fontSize: '14px',
          fontWeight: '500'
        }}
      >
        {/* Left side - Icon and message */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <CheckCircleOutlined 
            style={{ 
              fontSize: '18px',
              color: 'white'
            }} 
          />
          <div style={{ fontWeight: 'bold', fontSize: '15px' }}>
            ✅ Crisis Resolved - Lessons Learned template now available to finalise action on this crisis.
          </div>
        </div>

        {/* Right side - Action buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={handleComplete}
            style={{
              backgroundColor: 'white',
              color: '#52c41a',
              border: 'none',
              borderRadius: '4px',
              padding: '6px 12px',
              fontSize: '13px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f5f5f5';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'white';
            }}
          >
            <FileTextOutlined />
            Complete Lessons Learned
          </button>
          
          <button
            onClick={handleSkip}
            style={{
              backgroundColor: 'transparent',
              color: 'white',
              border: '1px solid rgba(255,255,255,0.5)',
              borderRadius: '4px',
              padding: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'rgba(255,255,255,0.2)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
            title="Skip lessons learned"
          >
            <CloseOutlined style={{ fontSize: '14px' }} />
          </button>
        </div>
      </div>
    </>
  );
};

export default LessonsLearnedBanner; 